import { useCallback, useMemo, useRef } from "react";
import { Button, Card, Flex, Text } from "@jsq/ds/core";
import { useFormContext } from "@jsq/ds/forms";
import { Sizes } from "@jsq/ds/styles";
import {
  type FormSectionConfig,
  type SectionActionHandle,
  type TaxFormValues,
} from "../../types";
import { DynamicField } from "../DynamicField";
import { LabelWithTooltip } from "../LabelWithTooltip";
import * as styles from "./styles_vanilla.css";

export type FormSectionProps = {
  section: FormSectionConfig;
  onSectionAction?: (sectionId: string) => void;
  header?: {
    title: string;
    subtitle?: string;
  };
};

export const FormSection = ({ section, onSectionAction, header }: FormSectionProps) => {
  const { values } = useFormContext<TaxFormValues>();
  const actionRef = useRef<SectionActionHandle | null>(null);

  const hasDraftEntries = section.fields.some((field) => {
    const value = values[field.name];
    return Array.isArray(value) && value.some((entry) => entry._isDraft);
  });

  const isVisible = useMemo(() => {
    if (!section.showWhen) return true;
    return section.showWhen(values);
  }, [section, values]);

  const sectionTitle = useMemo(() => {
    if (typeof section.title === "function") {
      return section.title(values);
    }
    return section.title;
  }, [section, values]);

  const handleActionClick = useCallback(() => {
    actionRef.current?.addEntry();
    if (section.onAction) {
      section.onAction();
    } else {
      onSectionAction?.(section.id);
    }
  }, [section, onSectionAction]);

  if (!isVisible) return null;

  const hasFields = section.fields.length > 0;
  const showEmptyState = !hasFields && section.emptyStateText;

  return (
    <Card data-testid={`section-${section.id}`}>
      {header && (
        <Flex flexDirection="column" className={styles.sectionHeaderContainer}>
          <Text variant="h2">{header.title}</Text>
          {header.subtitle && (
            <Text variant="secondary-md" className={styles.headerSubtitle}>
              {header.subtitle}
            </Text>
          )}
        </Flex>
      )}
      <Flex justifyContent="space-between" alignItems="center" className={styles.sectionHeader}>
        <Flex alignItems="baseline" gap={Sizes.XS}>
          {sectionTitle && (
            <Text variant="upper-md" className={styles.sectionTitle}>
              {sectionTitle}
            </Text>
          )}
          {section.tooltip && (
            <LabelWithTooltip fieldName={section.id} label={""} tooltip={section.tooltip} />
          )}
        </Flex>
        {section.actionLabel && !hasDraftEntries && (
          <Button displayAsLink onClick={handleActionClick}>
            {section.actionLabel}
          </Button>
        )}
      </Flex>
      {section.description && (
        <Text variant="secondary-sm" className={styles.sectionDescription}>
          {section.description}
        </Text>
      )}
      {showEmptyState ? (
        <Text variant="secondary-sm">{section.emptyStateText}</Text>
      ) : (
        <Flex flexWrap="wrap" gap={Sizes.MD}>
          {section.fields.map((field) => (
            <DynamicField
              key={`${section.id}-${field.name}`}
              field={field}
              actionRef={section.actionLabel ? actionRef : undefined}
            />
          ))}
        </Flex>
      )}
    </Card>
  );
};
